import { S3Client, ListObjectsCommand } from "@aws-sdk/client-s3";
import {useAwsCredentialsService} from "~/composables/service/useAwsCredentialsService";
import {useLearningStore} from "~/store/learning/learningStore";
import {applicationContext} from "@@/package/application/applicationContext";

export const useCertifyStaticAnalysisService = () => {
    const config = { public: applicationContext.getConfig() };
    const awsCredentialsService = useAwsCredentialsService();
    const learningStore = useLearningStore();
    const s3 = ref<S3Client>();

    /**
     * S3 인스턴스 초기화 (클라이언트 사이드에서만 실행)
     */
    const initS3 = async (): Promise<boolean> => {
        // 서버 사이드에서는 실행하지 않음
        if (typeof window === 'undefined') {
            return false;
        }

        // credentials가 없으면 먼저 생성
        const credentialsReady = await awsCredentialsService.ensureCredentials();
        if (!credentialsReady) {
            console.warn('Failed to ensure S3 credentials');
            return false;
        }

        if (!s3.value) {
            s3.value = new S3Client({
                region: 'ap-northeast-2',
                credentials: learningStore.directory.credentialProvider,
                useAccelerateEndpoint: true
            });
        }

        return true;
    };

    /**
     * 정적 분석 디렉토리 경로 생성
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     */
    const getAnalysisDirectoryPath = (domainId: number | string, userUuid: string): string => {
        return `reports/custom_assessment/${domainId}/${userUuid}`;
    };

    /**
     * S3에서 task ID 목록 조회
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     * @returns Promise<string[]> - task ID 배열
     */
    const getTaskIds = async (domainId: number | string, userUuid: string): Promise<string[]> => {
        const isInitialized = await initS3();
        if (!isInitialized) {
            throw new Error('S3 credentials not initialized or not in client environment');
        }

        const prefix = getAnalysisDirectoryPath(domainId, userUuid);
        const params = {
            Bucket: config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET,
            Prefix: `${prefix}/`,
            Delimiter: '/'
        };

        try {
            const data = await s3.value!.send(new ListObjectsCommand(params));

            if (data.CommonPrefixes) {
                const taskIds = data.CommonPrefixes
                    .map((item) => {
                        const fullPrefix = item.Prefix as string;
                        // "reports/custom_assessment/{domainId}/{userUuid}/{taskId}/" 형식에서 taskId만 추출
                        const parts = fullPrefix.split('/');
                        return parts[parts.length - 2]; // 마지막이 빈 문자열이므로 -2
                    })
                    .filter((id) => id && id.length > 0);
                return taskIds;
            }
            return [];
        } catch (err) {
            console.error('Failed to list task IDs:', err);
            throw err;
        }
    };

    /**
     * 특정 task의 S3 파일 목록 조회
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     * @param taskId - task ID
     * @returns Promise<string[]> - 파일 이름 배열
     */
    const getAnalysisFileList = async (domainId: number | string, userUuid: string, taskId: string): Promise<string[]> => {
        const isInitialized = await initS3();
        if (!isInitialized) {
            throw new Error('S3 credentials not initialized or not in client environment');
        }

        const prefix = `${getAnalysisDirectoryPath(domainId, userUuid)}/${taskId}`;
        const params = {
            Bucket: config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET,
            Prefix: prefix
        };

        try {
            const data = await s3.value!.send(new ListObjectsCommand(params));
            if(!data.Contents) data.Contents = [];

            if (data.Contents) {
                const fileList = data.Contents
                    .filter((object) => object.Key && object.Size && object.Size > 0)
                    .map((object) => (object.Key as string).replace(`${prefix}/`, ''));
                return fileList;
            }
            return [];
        } catch (err) {
            console.error('Failed to list analysis files:', err);
            throw err;
        }
    };

    /**
     * 모든 task의 분석 데이터 조회 (task별로 구조화)
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     * @returns Promise<Array<{taskId: string, files: string[]}>>
     */
    const getAllTasksAnalysis = async (
        domainId: number | string,
        userUuid: string
    ): Promise<Array<{taskId: string, files: string[]}>> => {
        try {
            const taskIds = await getTaskIds(domainId, userUuid);
            const tasksWithFiles = await Promise.all(
                taskIds.map(async (taskId) => {
                    const files = await getAnalysisFileList(domainId, userUuid, taskId);
                    const summaryFiles = files.filter(file => 
                        file.includes('summary_') || file === 'summary.json' || file === 'summary_ko.json'
                    );
                    return {
                        taskId,
                        files: summaryFiles
                    };
                })
            );
            // summary 파일이 있는 task만 반환
            return tasksWithFiles.filter(task => task.files.length > 0);
        } catch (error) {
            console.error('Error getting all tasks analysis:', error);
            return [];
        }
    };

    /**
     * 특정 분석 파일의 내용을 읽어옴
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     * @param taskId - task ID
     * @param fileName - 파일 이름 (예: 'summary.json')
     * @returns Promise<string> - 파일 내용
     */
    const getAnalysisFileContent = async (
        domainId: number | string,
        userUuid: string,
        taskId: string,
        fileName: string
    ): Promise<string | null> => {
        // 클라이언트 사이드에서만 실행
        if (typeof window === 'undefined') {
            console.warn('getAnalysisFileContent can only be called on client side');
            return null;
        }

        try {
            const prefix = getAnalysisDirectoryPath(domainId, userUuid);
            const cloudFrontDomain = config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET_CLOUDFRONT_DOMAIN;
            const cloudFrontUrl = `${cloudFrontDomain}/${prefix}/${taskId}/${fileName}`;

            const response = await fetch(cloudFrontUrl);
            
            if (!response.ok) {
                console.warn(`Failed to fetch file ${fileName}: ${response.statusText}`);
                return null;
            }

            const content = await response.text();
            return content;
        } catch (error) {
            console.error(`Error fetching file ${fileName}:`, error);
            return null;
        }
    };

    /**
     * 특정 task의 summary 파일 내용을 JSON으로 파싱하여 가져옴
     * @param domainId - assessment ID (certify_id)
     * @param userUuid - 사용자 UUID
     * @param taskId - task ID
     * @param lang - 언어 코드 (옵션)
     * @returns Promise<any> - summary JSON 데이터
     */
    const getSummaryData = async (
        domainId: number | string,
        userUuid: string,
        taskId: string,
        lang?: string
    ): Promise<any> => {
        try {
            // 우선순위: summary_{lang}.json > summary_ko.json > summary.json
            let fileName = 'summary.json';
            
            const fileList = await getAnalysisFileList(domainId, userUuid, taskId);
            
            if (lang && fileList.includes(`summary_${lang.toLowerCase()}.json`)) {
                fileName = `summary_${lang.toLowerCase()}.json`;
            } else if (fileList.includes('summary_ko.json')) {
                fileName = 'summary_ko.json';
            } else if (!fileList.includes('summary.json')) {
                console.warn('No summary file found');
                return null;
            }

            const content = await getAnalysisFileContent(domainId, userUuid, taskId, fileName);
            
            if (!content) {
                return null;
            }

            return JSON.parse(content);
        } catch (error) {
            console.error('Error getting summary data:', error);
            return null;
        }
    };

    /**
     * 서비스 초기화 (AWS Credentials 생성)
     * 필요시 명시적으로 호출할 수 있지만, 다른 함수들이 자동으로 호출하므로 필수는 아님
     */
    const initialize = async (): Promise<boolean> => {
        return await awsCredentialsService.ensureCredentials();
    };

    return {
        initialize,
        getTaskIds,
        getAllTasksAnalysis,
        getAnalysisFileList,
        getAnalysisFileContent,
        getSummaryData
    };
};

